// IAM Users — 멤버 목록(정렬 컬럼·역할 표시)·추가·상세·수정·퇴사 + 상단 조직도 연계 + 신청 승인.
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../shared/api/client";
import { Empty, Loading } from "../../../shared/components/Empty";
import { Pill } from "../../../shared/components/Pill";
import { toast } from "../../../shared/stores/toasts";

type User = {
  id: number; username: string | null; email: string; name: string;
  status: string; department_id: number | null; department_name: string | null;
  position_name: string | null; job_title_name: string | null; roles: string[];
};
type UserDetail = User & {
  name_en: string | null; phone: string | null; mobile: string | null;
  timezone: string; language: string; hire_date: string | null; last_login_at: string | null;
};
type Role = { id: number; code: string; name: string };
type Opt = { id: number; name: string };
type Dept = { id: number; name: string; level: number };
type DeptNode = { id: number; name: string; level: number; children: DeptNode[] };
type SortKey = "name" | "dept" | "title" | "role" | "status";
type SignupRequest = {
  id: number; name: string; email: string; desired_username: string | null;
  phone: string | null; dept_request: string | null; reason: string | null;
  status: string; review_note: string | null; created_at: string;
};
type SubTab = "users" | "pending";

const errMsg = (e: any) => e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "";
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 };
const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: "최고관리자", CEO: "대표", C_LEVEL: "임원", DIVISION_HEAD: "실장", MANAGER: "부서장",
  TEAM_LEAD: "팀장", HR_ADMIN: "인사관리자", FINANCE_ADMIN: "재무관리자", EMPLOYEE: "사원", INTERN: "인턴", CLIENT: "클라이언트", GUEST: "게스트",
};
const ROLE_RANK: Record<string, number> = {
  GUEST: 0, CLIENT: 5, INTERN: 10, EMPLOYEE: 20, TEAM_LEAD: 30, MANAGER: 40,
  FINANCE_ADMIN: 50, HR_ADMIN: 50, DIVISION_HEAD: 60, C_LEVEL: 80, CEO: 90, SUPER_ADMIN: 100,
};
const ROLE_COLOR: Record<string, string> = { SUPER_ADMIN: "#111827", CEO: "#9333ea", C_LEVEL: "#7c3aed", DIVISION_HEAD: "#dc2626", MANAGER: "#0891b2", TEAM_LEAD: "#2563eb" };
const roleRank = (rs: string[]) => Math.max(0, ...rs.map((r) => ROLE_RANK[r] ?? 0));
const GRID = "minmax(150px,1.6fr) 1.1fr 0.9fr 1.1fr 80px 56px";  // 이름·부서·직책·역할·상태·액션

function SortH({ label, k, sort, onSort }: { label: string; k: SortKey; sort: { key: SortKey; dir: "asc" | "desc" }; onSort: (k: SortKey) => void }) {
  const active = sort.key === k;
  return (
    <button onClick={() => onSort(k)} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, fontSize: 11.5, fontWeight: 700, textAlign: "left", display: "flex", alignItems: "center", gap: 3, color: active ? "var(--p-500)" : "var(--text-3)" }}>
      {label}{active && <span>{sort.dir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}

export function AdminUsers() {
  const [sub, setSub] = useState<SubTab>("users");
  return (
    <div className="t-grid cols-1">
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)" }}>
        {(["users", "pending"] as SubTab[]).map((t) => (
          <button key={t} onClick={() => setSub(t)}
            style={{ border: 0, background: "none", cursor: "pointer", padding: "8px 14px", fontSize: 13,
              fontWeight: sub === t ? 600 : 400, color: sub === t ? "var(--p-500)" : "var(--text-2)",
              borderBottom: sub === t ? "2px solid var(--p-500)" : "2px solid transparent" }}>
            {t === "users" ? "사용자 목록" : "신청 대기"}
          </button>
        ))}
      </div>
      {sub === "users" && <UsersList />}
      {sub === "pending" && <PendingList />}
    </div>
  );
}

// ── User list ─────────────────────────────────────────────────────────────────
function UsersList() {
  const [items, setItems] = useState<User[] | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [tree, setTree] = useState<DeptNode[]>([]);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [positions, setPositions] = useState<Opt[]>([]);
  const [jobTitles, setJobTitles] = useState<Opt[]>([]);
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState<number | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "name", dir: "asc" });
  const [orgOpen, setOrgOpen] = useState(true);

  const depts = useMemo(() => {
    const out: Dept[] = [];
    const walk = (ns: DeptNode[]) => ns.forEach((n) => { out.push({ id: n.id, name: n.name, level: n.level }); walk(n.children || []); });
    walk(tree); return out;
  }, [tree]);
  const nameOf = (id: number | null) => depts.find((d) => d.id === id)?.name ?? "";

  const reload = () => {
    const p = new URLSearchParams({ size: "300" });
    if (q) p.set("q", q);
    if (deptFilter != null) p.set("department_id", String(deptFilter));
    api.get<{ data: User[] }>(`/users?${p}`).then((r) => setItems(r.data.data ?? (r.data as any).items ?? [])).catch(() => setItems([]));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [q, deptFilter]);
  useEffect(() => {
    api.get<Role[]>("/iam/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get<DeptNode[]>("/iam/departments/tree").then((r) => setTree(r.data)).catch(() => {});
    api.get<Opt[]>("/iam/positions").then((r) => setPositions(r.data)).catch(() => {});
    api.get<Opt[]>("/iam/job-titles").then((r) => setJobTitles(r.data)).catch(() => {});
    api.get<{ department_id: number }[]>("/iam/org/members").then((r) => {
      const c: Record<number, number> = {};
      r.data.forEach((m) => { c[m.department_id] = (c[m.department_id] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    const v = (u: User): string | number => {
      switch (sort.key) {
        case "name": return u.name;
        case "dept": return u.department_name ?? "";
        case "title": return u.job_title_name ?? u.position_name ?? "";
        case "role": return roleRank(u.roles);
        case "status": return u.status;
        default: return u.name;
      }
    };
    arr.sort((a, b) => {
      const va = v(a), vb = v(b);
      const c = typeof va === "number" ? va - (vb as number) : String(va).localeCompare(String(vb), "ko");
      return sort.dir === "asc" ? c : -c;
    });
    return arr;
  }, [items, sort]);

  const onSort = (k: SortKey) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));

  const renderDept = (n: DeptNode, depth: number) => (
    <div key={n.id}>
      <button onClick={() => setDeptFilter((f) => (f === n.id ? null : n.id))}
        style={{
          display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", border: "none",
          background: deptFilter === n.id ? "rgba(99,102,241,0.12)" : "transparent", cursor: "pointer",
          padding: "4px 8px", paddingLeft: 8 + depth * 16, borderRadius: 6, fontSize: 12.5,
          color: deptFilter === n.id ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: deptFilter === n.id ? 600 : 400,
        }}>
        📁 {n.name} <span style={{ fontSize: 10.5, color: "var(--text-3)" }}>· {counts[n.id] ?? 0}명</span>
      </button>
      {n.children.map((c) => renderDept(c, depth + 1))}
    </div>
  );

  return (
    <>
      {/* 상단 조직도 — 부서 클릭 시 해당 팀원만 필터. 검색·버튼은 우측 상단에 작게. */}
      <div className="t-card" style={{ padding: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: orgOpen ? 8 : 0, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>🏢 조직도</span>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>부서 클릭 시 해당 팀원만 표시</span>
          {deptFilter != null && (
            <button className="t-btn" style={{ fontSize: 11, padding: "1px 8px" }} onClick={() => setDeptFilter(null)}>✕ {nameOf(deptFilter)} 해제</button>
          )}
          {/* 우측 상단: 작은 검색창 + 카운트 + 버튼 */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <input className="t-input" placeholder="🔍 이름·아이디" value={q} onChange={(e) => setQ(e.target.value)}
              style={{ width: 150, height: 27, fontSize: 12, padding: "2px 8px" }} />
            <span style={{ fontSize: 11.5, color: "var(--text-3)", whiteSpace: "nowrap" }}>{sorted.length}명</span>
            <button className="t-btn" style={{ fontSize: 11, padding: "4px 8px" }} onClick={() => setManaging(true)} title="직책·역할·권한 통합 관리">⚙ 직책·역할</button>
            <button className="t-btn t-btn-primary" style={{ fontSize: 11, padding: "4px 9px" }} onClick={() => setAdding(true)}>＋ 멤버</button>
            <button className="t-btn" style={{ fontSize: 11, padding: "4px 7px" }} onClick={() => setOrgOpen((o) => !o)} title={orgOpen ? "조직도 접기" : "조직도 펼치기"}>{orgOpen ? "▲" : "▼"}</button>
          </div>
        </div>
        {orgOpen && (
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <button onClick={() => setDeptFilter(null)}
              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 6, fontSize: 12.5,
                background: deptFilter == null ? "rgba(99,102,241,0.12)" : "transparent",
                fontWeight: deptFilter == null ? 600 : 400, color: deptFilter == null ? "var(--p-600,#4f46e5)" : "var(--text-2)" }}>
              🗂️ 전체 부서
            </button>
            {tree.map((n) => renderDept(n, 0))}
          </div>
        )}
      </div>

      {items === null && <Loading />}
      {items?.length === 0 && <Empty msg="해당 조건의 사용자가 없습니다" />}
      {items && items.length > 0 && (
        <div className="t-card" style={{ padding: 0, overflow: "hidden" }}>
          {/* 정렬 가능한 컬럼 헤더 */}
          <div style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, padding: "9px 14px", borderBottom: "2px solid var(--border)", background: "var(--bg-1,rgba(0,0,0,0.02))", alignItems: "center" }}>
            <SortH label="이름" k="name" sort={sort} onSort={onSort} />
            <SortH label="부서 / 팀" k="dept" sort={sort} onSort={onSort} />
            <SortH label="직책" k="title" sort={sort} onSort={onSort} />
            <SortH label="역할" k="role" sort={sort} onSort={onSort} />
            <SortH label="상태" k="status" sort={sort} onSort={onSort} />
            <span />
          </div>
          {sorted.map((u) => {
            const elevated = u.roles.filter((r) => r !== "EMPLOYEE").sort((a, b) => (ROLE_RANK[b] ?? 0) - (ROLE_RANK[a] ?? 0));
            return (
              <div key={u.id} onClick={() => setDetailId(u.id)} title="클릭: 세부 정보"
                style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, padding: "9px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", alignItems: "center", fontSize: 12.5 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <div className="t-av" style={{ background: "linear-gradient(135deg, var(--p-400), var(--p-600))", width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>{u.name.slice(0, 2)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
                    <div style={{ fontSize: 10.5, color: "var(--p-500)", fontFamily: "monospace" }}>@{u.username}</div>
                  </div>
                </div>
                <div style={{ color: "var(--text-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.department_name ?? "—"}</div>
                <div style={{ color: "var(--text-2)" }}>{u.job_title_name || u.position_name || "—"}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {elevated.length === 0 ? <span style={{ fontSize: 11, color: "var(--text-3)" }}>사원</span> :
                    elevated.slice(0, 2).map((r) => (
                      <span key={r} style={{ fontSize: 9.5, fontWeight: 700, color: "#fff", background: ROLE_COLOR[r] ?? "#6b7280", padding: "1px 6px", borderRadius: 8 }}>{ROLE_LABEL[r] ?? r}</span>
                    ))}
                  {elevated.length > 2 && <span style={{ fontSize: 10, color: "var(--text-3)" }}>+{elevated.length - 2}</span>}
                </div>
                <div><Pill kind={u.status === "ACTIVE" ? "ok" : "muted"}>{u.status}</Pill></div>
                <button className="t-btn" style={{ fontSize: 11, padding: "2px 7px" }} onClick={(e) => { e.stopPropagation(); setDetailId(u.id); }}>상세</button>
              </div>
            );
          })}
        </div>
      )}

      {detailId != null && (
        <UserDetailModal userId={detailId} roles={roles} depts={depts} positions={positions} jobTitles={jobTitles}
          onClose={() => setDetailId(null)} onChanged={reload} />
      )}
      {adding && (
        <AddMemberModal depts={depts} positions={positions} jobTitles={jobTitles}
          onClose={() => setAdding(false)} onCreated={() => { setAdding(false); reload(); }} />
      )}
      {managing && <JobRoleModal onClose={() => setManaging(false)} onChanged={reload} />}
    </>
  );
}

// ── 직책 · 역할 · 권한 통합 관리 ────────────────────────────────────────────────
type JobTitleFull = { id: number; code: string; name: string; is_manager: boolean; role_code: string | null };
type RoleFull = { id: number; code: string; name: string; permissions: { id: number; code: string; resource: string; action: string }[] };

function JobRoleModal({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const [jts, setJts] = useState<JobTitleFull[] | null>(null);
  const [rolesF, setRolesF] = useState<RoleFull[]>([]);
  const [busy, setBusy] = useState<number | "new" | "sync" | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);  // role code whose perms are shown
  const [nf, setNf] = useState({ code: "", name: "", role_code: "" });

  const loadJts = () => api.get<JobTitleFull[]>("/iam/job-titles").then((r) => setJts(r.data)).catch(() => setJts([]));
  useEffect(() => {
    loadJts();
    api.get<RoleFull[]>("/iam/roles").then((r) => setRolesF(r.data)).catch(() => {});
  }, []);

  const roleByCode = (c: string | null) => rolesF.find((r) => r.code === c) || null;
  const setMapping = async (jt: JobTitleFull, role_code: string) => {
    setBusy(jt.id);
    try {
      await api.patch(`/iam/job-titles/${jt.id}`, { role_code: role_code || null });
      toast.success(`${jt.name} → ${role_code ? (ROLE_LABEL[role_code] ?? role_code) : "역할 없음"}`, "해당 직책 구성원 권한이 재반영됐습니다");
      loadJts(); onChanged();
    } catch (e) { toast.error("변경 실패", errMsg(e)); } finally { setBusy(null); }
  };
  const addJt = async () => {
    if (!nf.code.trim() || !nf.name.trim()) return;
    setBusy("new");
    try {
      await api.post("/iam/job-titles", { code: nf.code.trim().toUpperCase(), name: nf.name.trim(), role_code: nf.role_code || null, is_manager: !!nf.role_code });
      toast.success("직책 추가됨"); setNf({ code: "", name: "", role_code: "" }); loadJts();
    } catch (e) { toast.error("추가 실패", errMsg(e)); } finally { setBusy(null); }
  };
  const recalcAll = async () => {
    setBusy("sync");
    try { const r = await api.post<{ synced: number }>("/iam/org/sync-roles", {}); toast.success("전체 역할 재계산 완료", `${r.data.synced}명 갱신`); onChanged(); }
    catch (e) { toast.error("재계산 실패", errMsg(e)); } finally { setBusy(null); }
  };

  // 역할 선택지: rank 순 정렬
  const roleOpts = [...rolesF].sort((a, b) => (ROLE_RANK[a.code] ?? 0) - (ROLE_RANK[b.code] ?? 0));
  const GRID2 = "1.3fr 1.5fr 60px 1.4fr";

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 640, maxWidth: "97vw", maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>⚙ 직책 · 역할 · 권한 통합 관리</h3>
          <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--text-3)" }}>직책마다 자동 부여할 <b>역할</b>을 지정하면, 그 직책 구성원의 <b>등급·권한</b>이 즉시 반영됩니다.</p>
        </div>

        <div style={{ padding: 18 }}>
          {/* 헤더 */}
          <div style={{ display: "grid", gridTemplateColumns: GRID2, gap: 8, padding: "0 6px 6px", fontSize: 11, fontWeight: 700, color: "var(--text-3)", borderBottom: "1px solid var(--border)" }}>
            <span>직책</span><span>매핑 역할 (자동 권한)</span><span>등급</span><span>권한</span>
          </div>
          {jts === null ? <div style={{ padding: 20 }}><Loading /></div> : jts.map((jt) => {
            const role = roleByCode(jt.role_code);
            const permCount = role?.permissions.length ?? 0;
            return (
              <div key={jt.id} style={{ borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "grid", gridTemplateColumns: GRID2, gap: 8, padding: "8px 6px", alignItems: "center", fontSize: 12.5 }}>
                  <div><b>{jt.name}</b> <span style={{ fontSize: 10, color: "var(--text-3)", fontFamily: "monospace" }}>{jt.code}</span></div>
                  <div>
                    <select className="t-input" value={jt.role_code ?? ""} disabled={busy === jt.id}
                      onChange={(e) => setMapping(jt, e.target.value)} style={{ height: 28, fontSize: 12, padding: "2px 6px" }}>
                      <option value="">(역할 없음)</option>
                      {roleOpts.map((r) => <option key={r.code} value={r.code}>{r.name} ({r.code})</option>)}
                    </select>
                  </div>
                  <div style={{ fontWeight: 700, color: jt.role_code ? "var(--p-500)" : "var(--text-3)" }}>{jt.role_code ? (ROLE_RANK[jt.role_code] ?? "-") : "-"}</div>
                  <div style={{ fontSize: 12 }}>
                    {role ? (
                      <button className="t-btn" style={{ fontSize: 11, padding: "2px 7px" }}
                        onClick={() => setExpanded((p) => (p === role.code ? null : role.code))}>
                        {permCount}개 권한 {expanded === role.code ? "▲" : "▼"}
                      </button>
                    ) : <span style={{ color: "var(--text-3)" }}>—</span>}
                  </div>
                </div>
                {role && expanded === role.code && (
                  <div style={{ padding: "4px 6px 10px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {role.permissions.length === 0 ? <span style={{ fontSize: 11, color: "var(--text-3)" }}>이 역할에 부여된 권한이 없습니다.</span> :
                      role.permissions.map((p) => (
                        <span key={p.id} title={p.code} style={{ fontSize: 10.5, background: "var(--bg-1,#f1f5f9)", borderRadius: 6, padding: "2px 7px", color: "var(--text-2)" }}>
                          {p.resource}:{p.action}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* 직책 추가 */}
          <div style={{ marginTop: 14, padding: 10, background: "var(--bg-1,rgba(0,0,0,0.02))", borderRadius: 8 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 6, color: "var(--text-2)" }}>＋ 직책 추가</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <input className="t-input" placeholder="코드 (예: VICE_HEAD)" value={nf.code} onChange={(e) => setNf((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") }))} style={{ width: 150, height: 28, fontSize: 12, fontFamily: "monospace" }} />
              <input className="t-input" placeholder="이름 (예: 부실장)" value={nf.name} onChange={(e) => setNf((p) => ({ ...p, name: e.target.value }))} style={{ width: 130, height: 28, fontSize: 12 }} />
              <select className="t-input" value={nf.role_code} onChange={(e) => setNf((p) => ({ ...p, role_code: e.target.value }))} style={{ width: 170, height: 28, fontSize: 12 }}>
                <option value="">매핑 역할 (선택)</option>
                {roleOpts.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
              </select>
              <button className="t-btn t-btn-primary" style={{ fontSize: 12 }} disabled={busy === "new" || !nf.code.trim() || !nf.name.trim()} onClick={addJt}>추가</button>
            </div>
          </div>

          <div style={{ fontSize: 10.5, color: "var(--text-3)", marginTop: 10 }}>
            ※ 역할별 세부 권한(체크박스) 편집은 <b>설정 → 역할/권한</b> 탭에서 관리합니다. 여기서는 직책→역할 매핑과 그 역할의 권한을 확인합니다.
          </div>
        </div>

        <div style={{ padding: "10px 18px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "center" }}>
          <button className="t-btn" disabled={busy === "sync"} onClick={recalcAll}>🔄 전체 역할 재계산</button>
          <button className="t-btn" style={{ marginLeft: "auto" }} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

// ── User detail / edit modal ──────────────────────────────────────────────────
function UserDetailModal({ userId, roles, depts, positions, jobTitles, onClose, onChanged }: {
  userId: number; roles: Role[]; depts: Dept[]; positions: Opt[]; jobTitles: Opt[];
  onClose: () => void; onChanged: () => void;
}) {
  const [d, setD] = useState<UserDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get<UserDetail>(`/users/${userId}`).then((r) => setD(r.data)).catch(() => toast.error("불러오기 실패"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const roleName = (code: string) => roles.find((r) => r.code === code)?.name ?? ROLE_LABEL[code] ?? code;
  const saveEmployment = async (department_id: number, position_id: number | null, job_title_id: number | null) => {
    setBusy(true);
    try { await api.patch(`/users/${userId}/employment`, { department_id, position_id, job_title_id }); toast.success("조직 정보 변경됨"); load(); onChanged(); }
    catch (e) { toast.error("변경 실패", errMsg(e)); } finally { setBusy(false); }
  };
  const saveProfile = async (patch: ProfilePatch) => {
    setBusy(true);
    try { await api.patch(`/users/${userId}`, patch); toast.success("정보 변경됨"); load(); onChanged(); }
    catch (e) { toast.error("변경 실패", errMsg(e)); } finally { setBusy(false); }
  };
  const resetPassword = async () => {
    const np = prompt("새 비밀번호를 입력하세요 (8자 이상).\n비워두면 임시 비밀번호가 자동 생성됩니다.", "");
    if (np === null) return;  // 취소
    const val = np.trim();
    if (val && val.length < 8) { toast.warn("비밀번호는 8자 이상이어야 합니다"); return; }
    if (!confirm(`${d?.name} 님의 비밀번호를 재설정합니다.\n기존 로그인 세션은 모두 로그아웃됩니다. 진행할까요?`)) return;
    try {
      const res = await api.post<{ password: string }>(`/users/${userId}/password`, { new_password: val || null });
      toast.success("비밀번호 재설정됨", `새 비밀번호: ${res.data.password}`);
      window.prompt("새 비밀번호입니다. 복사해 사용자에게 전달하세요:", res.data.password);
    } catch (e) { toast.error("재설정 실패", errMsg(e)); }
  };
  const grant = async (code: string) => { try { await api.post(`/iam/users/${userId}/roles`, { role_code: code }); toast.success("역할 부여"); load(); } catch (e) { toast.error("실패", errMsg(e)); } };
  const revoke = async (code: string) => { try { await api.delete(`/iam/users/${userId}/roles/${code}`); toast.success("역할 회수"); load(); } catch (e) { toast.error("실패", errMsg(e)); } };
  const retire = async () => {
    if (!confirm(`${d?.name} 님을 퇴사 처리하시겠습니까?\n계정이 비활성화됩니다.`)) return;
    try { await api.post(`/users/${userId}:retire`, { retire_date: new Date().toISOString().slice(0, 10) }); toast.success("퇴사 처리됨"); onChanged(); onClose(); }
    catch (e) { toast.error("퇴사 실패", errMsg(e)); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: "96vw", maxHeight: "92vh", overflow: "auto" }}>
        {!d ? <div style={{ padding: 30 }}><Loading /></div> : (
          <>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
              <div className="t-av" style={{ background: "linear-gradient(135deg, var(--p-400), var(--p-600))", width: 44, height: 44, fontSize: 16 }}>{d.name.slice(0, 2)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{d.name} {d.name_en && <span style={{ fontSize: 12, color: "var(--text-3)" }}>{d.name_en}</span>}</div>
                <div style={{ fontSize: 12, color: "var(--p-500)", fontFamily: "monospace" }}>@{d.username} · {d.email}</div>
              </div>
              <Pill kind={d.status === "ACTIVE" ? "ok" : "muted"}>{d.status}</Pill>
            </div>

            <div style={{ padding: 18, display: "grid", gap: 16 }}>
              <section>
                <SecTitle>기본 정보</SecTitle>
                <Info label="입사일" value={d.hire_date ?? "—"} />
                <Info label="최근 로그인" value={d.last_login_at ? new Date(d.last_login_at).toLocaleString("ko-KR") : "—"} />
                <Info label="연락처" value={[d.mobile, d.phone].filter(Boolean).join(" / ") || "—"} />
              </section>

              <EditEmployment d={d} depts={depts} positions={positions} jobTitles={jobTitles} busy={busy} onSave={saveEmployment} />
              <EditProfile d={d} busy={busy} onSave={saveProfile} />

              <section>
                <SecTitle>역할 / 권한</SecTitle>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {d.roles.length === 0 && <span style={{ fontSize: 12, color: "var(--text-3)" }}>부여된 역할 없음</span>}
                  {d.roles.map((c) => (
                    <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600, background: "var(--bg-1,#f1f5f9)", borderRadius: 14, padding: "3px 6px 3px 10px" }}>
                      {roleName(c)}
                      <button onClick={() => revoke(c)} title="회수" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", padding: "0 2px" }}>✕</button>
                    </span>
                  ))}
                </div>
                <select className="t-input" value="" onChange={(e) => e.target.value && grant(e.target.value)} style={{ maxWidth: 200, fontSize: 12 }}>
                  <option value="">＋ 역할 부여</option>
                  {roles.filter((r) => !d.roles.includes(r.code)).map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                </select>
                <div style={{ fontSize: 10.5, color: "var(--text-3)", marginTop: 6 }}>※ 조직도(직책·부서장)에서 자동 부여된 역할은 조직 변경 시 함께 갱신됩니다.</div>
              </section>
            </div>

            <div style={{ padding: "10px 18px", borderTop: "1px solid var(--border)", display: "flex", gap: 8 }}>
              <button className="t-btn" onClick={resetPassword}>🔑 비밀번호 재설정</button>
              <button className="t-btn" style={{ color: "var(--danger,#ef4444)" }} onClick={retire}>🗑️ 퇴사 처리</button>
              <button className="t-btn" style={{ marginLeft: "auto" }} onClick={onClose}>닫기</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditEmployment({ d, depts, positions, jobTitles, busy, onSave }: {
  d: UserDetail; depts: Dept[]; positions: Opt[]; jobTitles: Opt[]; busy: boolean;
  onSave: (dep: number, pos: number | null, jt: number | null) => void;
}) {
  const [dep, setDep] = useState<string>(d.department_id != null ? String(d.department_id) : "");
  const [pos, setPos] = useState<string>("");
  const [jt, setJt] = useState<string>("");
  useEffect(() => {
    setPos(positions.find((p) => p.name === d.position_name)?.id?.toString() ?? "");
    setJt(jobTitles.find((j) => j.name === d.job_title_name)?.id?.toString() ?? "");
  }, [d, positions, jobTitles]);
  return (
    <section>
      <SecTitle>조직 (변경 시 등급·권한 자동 반영)</SecTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div><label style={lbl}>부서</label>
          <select className="t-input" value={dep} onChange={(e) => setDep(e.target.value)}>
            {depts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></div>
        <div><label style={lbl}>직급</label>
          <select className="t-input" value={pos} onChange={(e) => setPos(e.target.value)}>
            <option value="">(없음)</option>{positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
        <div><label style={lbl}>직책</label>
          <select className="t-input" value={jt} onChange={(e) => setJt(e.target.value)}>
            <option value="">(없음)</option>{jobTitles.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select></div>
      </div>
      <button className="t-btn t-btn-primary" disabled={busy || !dep} style={{ marginTop: 8, fontSize: 12 }}
        onClick={() => onSave(Number(dep), pos === "" ? null : Number(pos), jt === "" ? null : Number(jt))}>조직 정보 저장</button>
    </section>
  );
}

type ProfilePatch = { name: string; name_en: string | null; email: string; mobile: string | null; phone: string | null; status: string };
function EditProfile({ d, busy, onSave }: { d: UserDetail; busy: boolean; onSave: (patch: ProfilePatch) => void }) {
  const [name, setName] = useState(d.name);
  const [nameEn, setNameEn] = useState(d.name_en ?? "");
  const [email, setEmail] = useState(d.email);
  const [mobile, setMobile] = useState(d.mobile ?? "");
  const [phone, setPhone] = useState(d.phone ?? "");
  const [status, setStatus] = useState(d.status);
  return (
    <section>
      <SecTitle>프로필 / 계정</SecTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div><label style={lbl}>이름</label><input className="t-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label style={lbl}>영문 이름</label><input className="t-input" value={nameEn} onChange={(e) => setNameEn(e.target.value)} /></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>이메일</label><input className="t-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div><label style={lbl}>휴대전화</label><input className="t-input" value={mobile} onChange={(e) => setMobile(e.target.value)} /></div>
        <div><label style={lbl}>전화</label><input className="t-input" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        <div><label style={lbl}>계정 상태</label>
          <select className="t-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ACTIVE">활성 (ACTIVE)</option>
            <option value="INACTIVE">비활성 (INACTIVE)</option>
          </select>
        </div>
      </div>
      <button className="t-btn" disabled={busy || !name.trim() || !email.trim()} style={{ marginTop: 8, fontSize: 12 }}
        onClick={() => onSave({ name: name.trim(), name_en: nameEn.trim() || null, email: email.trim(), mobile: mobile.trim() || null, phone: phone.trim() || null, status })}>정보 저장</button>
    </section>
  );
}

// ── Add member modal ──────────────────────────────────────────────────────────
function AddMemberModal({ depts, positions, jobTitles, onClose, onCreated }: {
  depts: Dept[]; positions: Opt[]; jobTitles: Opt[]; onClose: () => void; onCreated: () => void;
}) {
  const [f, setF] = useState({
    username: "", name: "", name_en: "", password: "", emp_no: "",
    department_id: depts[0]?.id ?? 0, position_id: "", job_title_id: "",
    hire_date: new Date().toISOString().slice(0, 10), mobile: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.username.trim() && f.name.trim() && f.password.length >= 8 && f.emp_no.trim() && f.department_id;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await api.post("/users", {
        username: f.username.trim(), name: f.name.trim(), name_en: f.name_en.trim() || null,
        password: f.password, emp_no: f.emp_no.trim(), department_id: f.department_id,
        position_id: f.position_id === "" ? null : Number(f.position_id),
        job_title_id: f.job_title_id === "" ? null : Number(f.job_title_id),
        hire_date: f.hire_date, mobile: f.mobile.trim() || null,
      });
      toast.success("멤버 추가됨");
      onCreated();
    } catch (e) { toast.error("추가 실패", errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "96vw", maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)" }}><h3 style={{ margin: 0, fontSize: 14 }}>＋ 멤버 추가</h3></div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>아이디 *</label><input className="t-input" value={f.username} onChange={(e) => set("username", e.target.value.replace(/[^a-zA-Z0-9_.-]/g, ""))} placeholder="hong.gd" style={{ fontFamily: "monospace" }} /></div>
            <div><label style={lbl}>사번 *</label><input className="t-input" value={f.emp_no} onChange={(e) => set("emp_no", e.target.value)} placeholder="EMP-2026-001" /></div>
            <div><label style={lbl}>이름 *</label><input className="t-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><label style={lbl}>영문 이름</label><input className="t-input" value={f.name_en} onChange={(e) => set("name_en", e.target.value)} /></div>
          </div>
          <div><label style={lbl}>초기 비밀번호 * <span style={{ fontWeight: 400 }}>(8자 이상)</span></label>
            <input className="t-input" type="text" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="최소 8자" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>부서 *</label>
              <select className="t-input" value={f.department_id} onChange={(e) => set("department_id", Number(e.target.value))}>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select></div>
            <div><label style={lbl}>직급</label>
              <select className="t-input" value={f.position_id} onChange={(e) => set("position_id", e.target.value)}>
                <option value="">(없음)</option>{positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label style={lbl}>직책</label>
              <select className="t-input" value={f.job_title_id} onChange={(e) => set("job_title_id", e.target.value)}>
                <option value="">(없음)</option>{jobTitles.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>입사일</label><input className="t-input" type="date" value={f.hire_date} onChange={(e) => set("hire_date", e.target.value)} /></div>
            <div><label style={lbl}>휴대전화</label><input className="t-input" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>직급/직책에 따라 등급·권한이 자동 부여됩니다 (예: 팀장→TEAM_LEAD).</div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" disabled={!valid || saving} onClick={submit}>{saving ? "추가 중…" : "추가"}</button>
        </div>
      </div>
    </div>
  );
}

function SecTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>{children}</div>;
}
function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "3px 0", fontSize: 12.5 }}>
      <span style={{ width: 80, color: "var(--text-3)", flexShrink: 0 }}>{label}</span>
      <span style={{ color: "var(--text-1)" }}>{value}</span>
    </div>
  );
}

// ── Pending signup requests ───────────────────────────────────────────────────
function PendingList() {
  const [items, setItems] = useState<SignupRequest[] | null>(null);
  const [approving, setApproving] = useState<Record<number, boolean>>({});

  const reload = () =>
    api.get<{ data: SignupRequest[] }>("/admin/signup-requests?status=PENDING&size=50")
      .then((r) => setItems(r.data.data ?? (r.data as any).items ?? []))
      .catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  const approve = async (item: SignupRequest) => {
    const suggested = item.desired_username || item.email.split("@")[0].replace(/[^a-zA-Z0-9_.]/g, "");
    const chosenUsername = window.prompt(`사용자 아이디(username)를 확인하거나 변경하세요.\n신청자: ${item.name} (${item.email})`, suggested);
    if (chosenUsername === null) return;
    if (!chosenUsername.trim()) { toast.warn("아이디를 입력해주세요"); return; }
    setApproving((p) => ({ ...p, [item.id]: true }));
    try {
      const res = await api.post<{ user_id: number; username: string; email: string; temp_password: string }>(
        `/admin/signup-requests/${item.id}/approve`, { username: chosenUsername.trim() });
      toast.success("승인 완료", `아이디: ${res.data.username} · 임시 비밀번호: ${res.data.temp_password}`);
      reload();
    } catch (e: any) { toast.error("승인 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setApproving((p) => ({ ...p, [item.id]: false })); }
  };
  const reject = async (id: number) => {
    const note = window.prompt("반려 사유 (선택)");
    if (note === null) return;
    try { await api.post(`/admin/signup-requests/${id}/reject`, { note: note || null }); toast.success("반려 처리됨"); reload(); }
    catch (e: any) { toast.error("반려 실패", e?.response?.data?.error?.message ?? ""); }
  };

  if (items === null) return <Loading />;
  if (items.length === 0) return <Empty msg="대기 중인 신청이 없습니다" />;

  return (
    <div className="t-card" style={{ padding: 0 }}>
      <div className="t-list" style={{ padding: 16 }}>
        {items.map((r) => (
          <div key={r.id} className="t-row" style={{ alignItems: "flex-start", gap: 12 }}>
            <div className="t-av" style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)", marginTop: 2 }}>{r.name.slice(0, 2)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{r.name}<span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-3)" }}>{r.email}</span></div>
              {r.desired_username && (
                <div style={{ fontSize: 12, color: "var(--p-500)", marginTop: 2 }}>희망 아이디: <span style={{ fontFamily: "monospace", fontWeight: 600 }}>@{r.desired_username}</span></div>
              )}
              <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                {r.dept_request && <span>희망부서: {r.dept_request} · </span>}
                {r.phone && <span>{r.phone} · </span>}
                <span>{new Date(r.created_at).toLocaleDateString("ko-KR")}</span>
              </div>
              {r.reason && <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 4, fontStyle: "italic" }}>"{r.reason}"</div>}
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button className="t-btn solid" style={{ fontSize: 12, padding: "4px 10px", opacity: approving[r.id] ? 0.5 : 1 }} disabled={approving[r.id]} onClick={() => approve(r)}>
                {approving[r.id] ? "처리 중…" : "승인"}
              </button>
              <button className="t-btn" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => reject(r.id)}>반려</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
